<script setup lang="ts">
const props = defineProps<{
  isValidForm: boolean;
  isLoading: boolean;
  isSubscription: boolean;
  globalError: string | null;
  organizationName: string;
  isPreviewMode?: boolean;
  submitText?: string | null;
  primaryColor?: string | null;
  buttonStyle?: string | null;
  /**
   * Label des providers actifs ("Stripe", "PayPal" ou "Stripe et PayPal").
   * Utilisé pour la mention de sécurité sous le bouton. Si non fourni,
   * fallback générique sans nom de provider.
   */
  providersLabel?: string | null;
  /**
   * Masque la mention de sécurité sous le bouton (« Paiement sécurisé par… » /
   * « Payez en toute sécurité à… »). Utilisé par le widget embed quand l'asso
   * choisit de la retirer. Défaut : affichée.
   */
  hideSecureNote?: boolean;
  /**
   * « KvK 12345678 », « RNA W123456789 » : numéro officiel de l'association,
   * exigé par iDEAL | Wero quand elle le propose (règle iDEAL). Affiché en
   * bas, sous « Paiement sécurisé par… » (Karim 02/11/2026), pas sous le bouton.
   */
  identifiantOfficiel?: string | null;
}>();

const { t } = useI18n();

const buttonRoundingClass = computed(() => {
  switch (props.buttonStyle) {
    case 'pill': return 'rounded-full';
    case 'square': return 'rounded-none';
    default: return 'rounded-lg';
  }
});

// Texte du bouton
const buttonText = computed(() => {
  if (props.submitText) return props.submitText;
  return props.isSubscription ? t("don.boutonSoumission.sabonner") : t("commun.actions.faireUnDon");
});

const emit = defineEmits<{
  validateAll: [];
}>();

// État pour l'animation squizz
const isShaking = ref(false);

const triggerShake = () => {
  isShaking.value = true;
  setTimeout(() => { isShaking.value = false; }, 500);
};

// Squizz rouge aussi quand une erreur arrive après soumission (CVC incomplet, etc.)
watch(() => props.globalError, (err) => {
  if (err) triggerShake();
});

// Gérer le clic sur le bouton
const handleClick = () => {
  if (props.isLoading) return;

  if (!props.isValidForm) {
    emit("validateAll");
    triggerShake();
  }
};
</script>

<template>
  <!-- L'erreur globale (paiement refusé, etc.) s'affiche directement
       sur le bloc carte (bordure rouge + message discret), pas en
       grosse bannière ici. UX cohérente avec les erreurs d'inputs.
       Karim 2026-06-28. -->

  <!-- Bouton de paiement — bleu Donoor uni comme les boutons de montant
       quand l'asso n'a pas choisi de couleur (Karim 2026-10-21). -->
  <button
    type="submit"
    :disabled="isLoading || isPreviewMode"
    :class="[
      'w-full py-3 px-4 font-medium text-white transition-all duration-200',
      buttonRoundingClass,
      isPreviewMode ? (primaryColor ? 'cursor-default opacity-90' : 'bg-(--color-main) cursor-default opacity-90') : 'cursor-pointer',
      isShaking ? 'animate-shake bg-red-500' : '',
      !isShaking && !isPreviewMode && !primaryColor
        ? 'bg-(--color-main) hover:bg-(--color-main-deep) focus:ring-4 focus:ring-blue-200'
        : '',
    ]"
    :style="primaryColor && !isShaking ? { background: primaryColor } : undefined"
    @click="handleClick"
  >
    <span v-if="isLoading" class="flex items-center justify-center">
      <svg
        class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {{ t('don.boutonSoumission.traitementEnCours') }}
    </span>
    <span v-else>{{ buttonText }}</span>
  </button>

  <!-- Message de sécurité — adapté au provider actif de l'org. Masquable. -->
  <div v-if="!hideSecureNote" class="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
    <template v-if="providersLabel">
      {{ t('don.boutonSoumission.paiementSecuriseParProvider', { provider: providersLabel }) }}
    </template>
    <template v-else>
      {{ t('don.boutonSoumission.payerEnSecuriteA', { nom: organizationName }) }}
    </template>
    <span v-if="identifiantOfficiel" class="block mt-0.5">
      {{ organizationName }} · <span data-testid="identifiant-officiel">{{ identifiantOfficiel }}</span>
    </span>
  </div>
</template>

<style scoped>
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-4px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(4px);
  }
}

.animate-shake {
  animation: shake 0.5s ease-in-out;
}
</style>
